/**
 * Invoice Preview Component
 * * Renders a classic A4 invoice with dynamic branding from organization settings.
 * Supports print mode and displays ForgeDash footer (white-label for premium tiers).
 * * All business logic and data fetching handled by Supabase.
 * Frontend remains dumb - only displays what DB provides.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { Printer, Download, Loader2, AlertCircle, Send } from 'lucide-react';
import EmailInvoiceModal from './EmailInvoiceModal';
import { pdf } from '@react-pdf/renderer';
import { InvoicePDF } from './InvoicePDF';
import { urlToBase64, LOGO_MAX_PX } from '../../../lib/images';
import { uploadInvoicePDF } from '../../../lib/invoice-storage';
import { extraWorkInvoiceNote, type ExtraWorkSource } from '../../../lib/extraWork';
import { isWhiteLabelTier, canUseBankDetails, type SubscriptionTier } from '../../../lib/tiers';
import { reportError, errorCode } from '../../../lib/errors';
import toast from 'react-hot-toast';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

// Types matching DB schema
interface InvoiceLineItem {
    id: string;
    description: string;
    item_type: 'part' | 'labour' | 'service';
    quantity: number;
    unit_price: number;
    line_total: number;
    /** The extra-work request this line came from, if any (source_quote_id). */
    approval?: ExtraWorkSource | null;
}

interface InvoiceData {
    id: string;
    org_id: string;
    customer_id: string | null;
    invoice_number: string;
    status: 'draft' | 'issued' | 'partially_paid' | 'paid' | 'void' | 'refunded';
    subtotal: number;
    vat_amount: number;
    total_gross: number;
    amount_paid: number;
    balance_due: number;
    issued_at: string | null;
    due_date: string | null;
    notes: string | null;
    created_at: string;
    branding_snapshot: {
        business_name: string;
        logo_url: string;
        address: string;
        phone: string;
        email: string;
        website: string;
        invoice_accent_color: string;
        invoice_theme?: string;
        vat_number: string;
        is_vat_registered: boolean;
        payment_terms_days?: number;
        // Was `'free' | 'pro' | 'enterprise'` -- tier names that existed nowhere
        // in the database. `isWhiteLabelTier` tested for 'autopilot'/'full_auto',
        // so by this union it could never return true; it worked only because
        // the runtime value never matched the declared type.
        //
        // Note the irony now that the 2026-08-10 rename has landed: 'pro' IS a
        // real tier at last, and 'enterprise' still is not. Use the shared type
        // so the two cannot drift apart again.
        subscription_tier: SubscriptionTier;
        bank_details?: {
            bank_name: string;
            account_name: string;
            sort_code: string;
            account_number: string;
        };
    };
    customers?: { full_name: string; email?: string; phone?: string };
    organizations?: { name: string };
    vehicles?: { vrm: string; make: string; model: string; engine?: string; fuel_type?: string };
    invoice_line_items: InvoiceLineItem[];
}

interface InvoicePreviewProps {
    invoiceId: string;
    onPaymentClick?: (invoice: InvoiceData, mode: 'card' | 'cash' | 'part') => void;
}

export default function InvoicePreview({ invoiceId, onPaymentClick }: InvoicePreviewProps) {
    const [invoice, setInvoice] = useState<InvoiceData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sendingEmail, _setSendingEmail] = useState(false);
    const [downloadingPDF, setDownloadingPDF] = useState(false);
    const [logoBase64, setLogoBase64] = useState<string | null>(null);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

    /**
     * Load the invoice and return the fresh row.
     *
     * Returns the data rather than only setting state because React state is one
     * render behind: a caller that refetches and then immediately renders a PDF
     * would still print the previous row. Callers that are about to produce a
     * document use the return value; callers that just want the screen updated
     * ignore it.
     *
     * `surfaceErrors` decides whether a failure reaches the screen. Only the
     * initial load sets it: a background refresh that fails must not replace an
     * invoice the user is already reading with an error panel. Every failure is
     * reported to Sentry either way.
     *
     * Nothing is set on state before the first `await`, deliberately. This runs
     * from an effect, and a synchronous setState there is what
     * react-hooks/set-state-in-effect flags — the loading spinner does not need
     * one anyway, because `loading` already initialises to `true` and this only
     * ever turns it off.
     */
    const loadInvoice = useCallback(async ({ surfaceErrors = false } = {}): Promise<InvoiceData | null> => {
        if (!invoiceId) return null;

        // `email` and `phone` are NOT embedded as `customers(email, phone)`.
        // Those are the write-side columns: a BEFORE trigger encrypts them
        // into email_encrypted/phone_encrypted (20260810211000) and the purge
        // script empties the originals, so an embedded read returns NULL for
        // every customer who has contact details. That is what made "Email
        // invoice" answer "This customer does not have an email address on
        // file" for customers who plainly did — and printed invoices with a
        // blank contact block. Reads go through customers_decrypted.
        const { data, error: fetchError } = await supabase
            .from('invoices')
            .select(`
                *,
                customers ( full_name ),
                organizations ( name ),
                vehicles ( vrm, make, model, engine, fuel_type ),
                invoice_line_items ( id, description, item_type, quantity, unit_price, line_total, approval:quotes!source_quote_id ( quote_type, sequence_no, accepted_at ) )
            `)
            .eq('id', invoiceId)
            .single();

        if (fetchError) {
            reportError(fetchError, 'invoice-preview.fetch', { invoiceId });
            if (surfaceErrors) {
                setError(fetchError.message);
                setLoading(false);
            }
            return null;
        }

        let invoiceData = data as InvoiceData;

        if (data?.customer_id) {
            const { data: contact, error: contactError } = await supabase
                .from('customers_decrypted')
                .select('email, phone')
                .eq('id', data.customer_id)
                .maybeSingle();

            if (contactError) {
                // Not fatal — the invoice still renders, it just loses the
                // contact block and "Email invoice" will refuse. Worth knowing
                // about: this read is the one the PII encryption work rerouted.
                reportError(contactError, 'invoice-preview.customer-contact', {
                    invoiceId,
                    customerId: data.customer_id,
                });
            }

            if (contact) {
                invoiceData = {
                    ...invoiceData,
                    customers: {
                        full_name: invoiceData.customers?.full_name ?? '',
                        email: contact.email ?? undefined,
                        phone: contact.phone ?? undefined,
                    },
                };
            }
        }

        setError(null);
        setInvoice(invoiceData);

        // 2. Fetch Logo Base64 (Bypass CORS for PDF)
        if (data?.branding_snapshot?.logo_url) {
            urlToBase64(data.branding_snapshot.logo_url, { maxDimension: LOGO_MAX_PX })
                .then(base64 => setLogoBase64(base64))
                .catch(err => reportError(err, 'invoice-preview.logo-convert', { invoiceId }));
        }

        setLoading(false);
        return invoiceData;
    }, [invoiceId]);

    useEffect(() => {
        // A false positive, and worth stating why rather than restructuring the
        // component around it. The rule fires because `loadInvoice` is invoked
        // synchronously from the effect body and its definition contains
        // setState calls. It does not model the `await` boundary: every one of
        // those calls happens AFTER the first `await supabase...`, so none of
        // them runs during the effect and none can cascade a render.
        //
        // The effect below is the evidence — the identical call on the
        // visibilitychange listener is NOT flagged, because the rule reacts to
        // the call site, not to anything the function actually does.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        void loadInvoice({ surfaceErrors: true });
    }, [loadInvoice]);

    /**
     * Re-read the invoice whenever this tab becomes active again.
     *
     * Paying by card leaves the app entirely — Stripe Checkout is a redirect —
     * and the row does not change at the moment the customer pays. It changes
     * when the Connect webhook settles, which is asynchronous and was observed
     * ~15 minutes later during the 2026-08-17 end-to-end test. So there is no
     * single "payment succeeded" moment this component can hook; the honest
     * trigger is "the user is looking at this again, check whether it moved".
     */
    useEffect(() => {
        const refreshIfVisible = () => {
            if (document.visibilityState === 'visible') void loadInvoice();
        };
        document.addEventListener('visibilitychange', refreshIfVisible);
        window.addEventListener('focus', refreshIfVisible);
        return () => {
            document.removeEventListener('visibilitychange', refreshIfVisible);
            window.removeEventListener('focus', refreshIfVisible);
        };
    }, [loadInvoice]);

    // 3. Auto-save to storage if missing
    //
    // Guarded by a ref because `loadInvoice` hands back a new object on every
    // call, so the refocus refresh above changes this effect's `invoice`
    // identity each time the tab is activated. Without the guard, an invoice
    // whose `pdf_storage_path` is null — which is every invoice touched by the
    // 20260817153000 path cleanup — would re-render and re-upload its PDF on
    // every single focus event.
    const autoSavedRef = useRef<string | null>(null);

    useEffect(() => {
        const autoSave = async () => {
            if (!invoice || (invoice as any).pdf_storage_path) return;
            if (autoSavedRef.current === invoice.id) return;
            autoSavedRef.current = invoice.id;

            const pdfBase64 = await generatePdfBase64(invoice);
            if (pdfBase64) {
                try {
                    await uploadInvoicePDF(invoice.org_id, invoice.id, invoice.invoice_number, pdfBase64);
                } catch (err) {
                    // Let a later attempt retry rather than pinning the guard on
                    // a failure the user never saw.
                    autoSavedRef.current = null;
                    reportError(err, 'invoice-preview.autosave-upload', { invoiceId: invoice.id });
                }
            } else {
                autoSavedRef.current = null;
            }
        };

        if (!loading && invoice && logoBase64 !== null) {
            void autoSave();
        }
    }, [invoice, loading, logoBase64]);

    /**
     * Printing or downloading hands the invoice over just as emailing does, so
     * a draft is issued first: otherwise an invoice given on paper never gets
     * issued_at or a due date, and drops out of the customer's balance and the
     * payment-due reminders. fn_issue_invoice owns the transition; P0001 means
     * it is already issued, which is the state we want.
     */
    const issueIfDraft = async (target: InvoiceData) => {
        if (target.status !== 'draft') return;
        const { error } = await supabase.rpc('fn_issue_invoice', { p_invoice_id: target.id });
        if (error && errorCode(error) !== 'P0001') {
            reportError(error, 'invoice-preview.issue-on-handover', { invoiceId: target.id });
        }
    };

    // Bypasses popup blockers and broken HTML print engines
    const handlePrint = async () => {
        if (!invoice) return;
        try {
            setDownloadingPDF(true);

            // 1. Audit Log (non-blocking, correct Supabase syntax)
            const { error: auditError } = await supabase.rpc('log_manual_audit', {
                p_action_type: 'INVOICE_PRINTED',
                p_org_id: invoice.org_id,
                p_invoice_id: invoice.id
            });
            if (auditError) reportError(auditError, 'invoice-preview.audit-print', { invoiceId: invoice.id });

            // 2. Open tab immediately to beat the popup blocker
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                toast.error("Please allow pop-ups to print the invoice.");
                setDownloadingPDF(false);
                return;
            }

            printWindow.document.write('<html><body style="font-family:sans-serif; text-align:center; padding-top:50px;">Generating High-Quality Print Document...</body></html>');

            // 3. Issue a draft, then generate and inject the PDF from a FRESH
            // read — see handleDownloadPDF — so it carries the issue and due dates.
            await issueIfDraft(invoice);
            const fresh = (await loadInvoice()) ?? invoice;
            const blob = await pdf(<InvoicePDF invoice={fresh} logoBase64={logoBase64 || undefined} />).toBlob();
            const url = URL.createObjectURL(blob);
            printWindow.location.href = url;

        } catch (err) {
            reportError(err, 'invoice-preview.print', { invoiceId: invoice.id });
            toast.error('Failed to generate print document. Please use download instead.');
        } finally {
            setDownloadingPDF(false);
        }
    };

    const handleDownloadPDF = async () => {
        if (!invoice || downloadingPDF) return;

        try {
            setDownloadingPDF(true);

            // Audit log (non-blocking, correct Supabase syntax)
            const { error: auditError } = await supabase.rpc('log_manual_audit', {
                p_action_type: 'INVOICE_DOWNLOADED',
                p_org_id: invoice.org_id,
                p_invoice_id: invoice.id
            });
            if (auditError) reportError(auditError, 'invoice-preview.audit-download', { invoiceId: invoice.id });

            // Re-read before rendering. A downloaded PDF is the artefact the
            // customer keeps, so it must never be built from a row this
            // component happened to load minutes ago. This is the reported
            // 2026-08-17 symptom: an invoice downloaded shortly after payment
            // still carried "balance due", because the preview had been open
            // since before the Connect webhook settled and never re-read.
            await issueIfDraft(invoice);
            const fresh = (await loadInvoice()) ?? invoice;

            const blob = await pdf(<InvoicePDF invoice={fresh} logoBase64={logoBase64 || undefined} />).toBlob();

            // Download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice_${fresh.invoice_number}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            reportError(err, 'invoice-preview.download', { invoiceId: invoice.id });
            toast.error('Failed to generate PDF.');
        } finally {
            setDownloadingPDF(false);
        }
    };

    /**
     * Render a PDF to base64.
     *
     * Takes the invoice explicitly rather than reading state, and deliberately
     * does NOT refetch: its caller in the auto-save effect runs in response to
     * `invoice` changing, so a refetch here would set state, retrigger the
     * effect and loop.
     */
    const generatePdfBase64 = async (source?: InvoiceData): Promise<string | null> => {
        const target = source ?? invoice;
        if (!target) return null;
        try {
            const blob = await pdf(<InvoicePDF invoice={target} logoBase64={logoBase64 || undefined} />).toBlob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    const base64 = base64String.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (err) {
            reportError(err, 'invoice-preview.pdf-base64', { invoiceId: target.id });
            return null;
        }
    };

    const handleEmailInvoice = async () => {
        if (!invoice) return;
        if (!invoice.customers?.email) {
            toast.error('This customer does not have an email address on file.');
            return;
        }

        try {
            const { error: auditError } = await supabase.rpc('log_manual_audit', {
                p_action_type: 'INVOICE_EMAIL_INITIATED',
                p_org_id: invoice.org_id,
                p_invoice_id: invoice.id
            });
            if (auditError) reportError(auditError, 'invoice-preview.audit-email', { invoiceId: invoice.id });
        } catch (err) {
            reportError(err, 'invoice-preview.audit-email', { invoiceId: invoice.id });
        }

        setIsEmailModalOpen(true);
    };

    const handleEmailSuccess = async () => {
        if (!invoice) return;

        // Emailing only advances a DRAFT. Anything further along the state
        // machine (issued / partially_paid / paid / void / refunded) keeps its
        // status: a raw update here previously rewound already-paid invoices
        // back to 'issued', which left them looking unpaid and mis-bucketed the
        // job's parts in the working-capital reserve.
        if (invoice.status !== 'draft') {
            setIsEmailModalOpen(false);
            return;
        }

        try {
            // fn_issue_invoice owns the transition — it sets issued_at, derives
            // due_date from the org's terms, and enforces the draft-only rule.
            const { error } = await supabase.rpc('fn_issue_invoice', {
                p_invoice_id: invoice.id,
            });

            if (error) throw error;

            // Re-read rather than patching status locally: fn_issue_invoice also
            // sets issued_at and derives due_date, and the printed invoice shows
            // both. Patching only `status` left the preview claiming terms it had
            // not actually been given.
            const fresh = await loadInvoice();
            if (!fresh) setInvoice(prev => (prev ? { ...prev, status: 'issued' as const } : null));
            setIsEmailModalOpen(false);

        } catch (err) {
            // P0001 "Only draft invoices can be issued" means the invoice is
            // ALREADY issued — the end state we wanted. It happens whenever the
            // local `invoice` in this closure is a step behind the database:
            // re-sending, or a second send racing the first. Reporting it woke
            // Sentry and told the user their email had half-failed when in fact
            // everything had worked. Reconcile and move on.
            if (errorCode(err) === 'P0001') {
                await loadInvoice();
                setIsEmailModalOpen(false);
                return;
            }
            reportError(err, 'invoice-preview.issue-on-email', { invoiceId: invoice.id });
            toast.error('Email sent, but failed to update status.');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
        );
    }

    if (error || !invoice) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
                <AlertCircle className="w-10 h-10 text-danger" />
                <p className="text-danger text-sm">{error || 'Invoice not found'}</p>
            </div>
        );
    }

    const branding = invoice.branding_snapshot;
    const accentColor = branding.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT;
    const isMinimal = branding.invoice_theme === 'minimal';
    const garageName = invoice.organizations?.name || branding.business_name;
    const isWhiteLabel = isWhiteLabelTier(branding.subscription_tier);
    const showBankDetails = !!branding.bank_details?.bank_name && canUseBankDetails(branding.subscription_tier);
    const showPayButton = ['draft', 'issued', 'partially_paid'].includes(invoice.status) && invoice.balance_due > 0;

    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Toolbar */}
            {/* Emailing the customer is what this screen is FOR — it is also
                what quietly issues the invoice (sets issued_at, derives the due
                date). It used to sit in the same weight as Print and Download,
                so the one action that moves the invoice forward looked optional.
                Solid accent, first, full width on a phone. */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 mb-4">
                <button
                    onClick={handleEmailInvoice}
                    disabled={sendingEmail}
                    className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-2 px-5 py-3 fd-btn-primary rounded-[var(--fds-radius)] text-sm font-bold transition-all disabled:opacity-50"
                >
                    <Send className="w-4 h-4" />
                    Email to customer
                </button>
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={handlePrint}
                        disabled={downloadingPDF}
                        className="flex-1 sm:flex-none min-h-[44px] flex items-center justify-center gap-2 px-4 py-2 bg-tint/5 border border-tint/10 rounded-[var(--fds-radius)] text-sm font-medium text-fg-4 hover:bg-tint/10 transition-all disabled:opacity-50"
                    >
                        {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                        Print
                    </button>
                    <button
                        onClick={handleDownloadPDF}
                        disabled={downloadingPDF}
                        className="flex-1 sm:flex-none min-h-[44px] flex items-center justify-center gap-2 px-4 py-2 bg-tint/5 border border-tint/10 rounded-[var(--fds-radius)] text-sm font-medium text-fg-4 hover:bg-tint/10 transition-all disabled:opacity-50"
                    >
                        {downloadingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {downloadingPDF ? 'Generating…' : 'Download'}
                    </button>
                </div>
            </div>

            {/* Invoice Document Preview */}
            <div
                data-theme="paper" className="bg-card text-fg rounded-lg shadow-2xl overflow-hidden"
                style={{ '--invoice-accent': accentColor } as React.CSSProperties}
            >
                {/* Header Container */}
                <div
                    className="border-b border-line"
                    style={isMinimal ? { borderTop: `4px solid ${accentColor}` } : { borderTop: `8px solid ${accentColor}` }}
                >
                    {/* DECOUPLED LOGO ROW */}
                    {(branding.logo_url || logoBase64) && (
                        <div className="px-4 sm:px-8 pt-5 sm:pt-8 pb-2">
                            <img
                                src={logoBase64 || branding.logo_url}
                                alt="Business Logo"
                                crossOrigin="anonymous"
                                className="w-14 h-14 object-contain rounded-lg shadow-sm"
                            />
                        </div>
                    )}

                    {/* Natural mobile sections; the A4-style columns return at sm+. */}
                    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8 px-4 sm:px-8 pb-5 sm:pb-8 ${!(branding.logo_url || logoBase64) ? 'pt-5 sm:pt-8' : 'pt-3 sm:pt-4'}`}>
                        {/* Col 1: Identity */}
                        <div>
                            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-fg-3 mb-2.5">Invoice From:</p>
                            <h1 className="text-lg font-bold text-fg mb-1">{garageName}</h1>
                            {(branding.address || branding.phone || branding.email) && (
                                <div className="mt-2 text-[13px] text-fg-2 space-y-0.5 leading-relaxed">
                                    {branding.address && <p className="whitespace-pre-line">{branding.address.replace(/\\n/g, '\n')}</p>}
                                    {branding.phone && <p>{branding.phone}</p>}
                                    {branding.email && <p>{branding.email}</p>}
                                    {branding.vat_number && <p className="pt-1.5 text-xs text-fg-3">VAT: {branding.vat_number}</p>}
                                </div>
                            )}
                        </div>

                        {/* Col 2: Invoice To */}
                        <div>
                            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-fg-3 mb-2.5">Invoice To:</p>
                            <p className="font-bold text-[15px] text-fg mb-1">{invoice.customers?.full_name || 'Customer'}</p>
                            <div className="text-[13px] text-fg-2 space-y-0.5">
                                {invoice.customers?.email && <p>{invoice.customers.email}</p>}
                                {invoice.customers?.phone && <p>{invoice.customers.phone}</p>}
                                {invoice.vehicles && (
                                    <p className="mt-3 pt-2 border-t border-line text-fg-3">
                                        Vehicle: <span className="font-mono font-bold text-fg">{invoice.vehicles.vrm}</span>
                                        {' '}({invoice.vehicles.make} {invoice.vehicles.model}{invoice.vehicles.engine ? ` - ${invoice.vehicles.engine}` : ''}{invoice.vehicles.fuel_type ? ` (${invoice.vehicles.fuel_type})` : ''})
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Col 3: Invoice Meta */}
                        <div className="text-left sm:text-right border-t border-line pt-4 sm:border-0 sm:pt-0">
                            <h2 className="text-2xl font-black uppercase tracking-[0.2em] text-fg mb-0.5">Invoice</h2>
                            <p className="text-[13px] font-bold text-fg-3 mb-5">{invoice.invoice_number}</p>

                            <div className="flex flex-col items-start sm:items-end gap-1.5 text-[13px]">
                                <div className="flex justify-between sm:justify-end gap-3 w-full sm:w-48">
                                    <span className="text-fg-3">Date:</span>
                                    <span className="font-medium text-fg text-right w-24">{formatDate(invoice.issued_at || invoice.created_at)}</span>
                                </div>
                                <div className="flex justify-between sm:justify-end gap-3 w-full sm:w-48">
                                    <span className="text-fg-3">Due:</span>
                                    <span className="font-medium text-fg text-right w-24">
                                        {formatDate(invoice.due_date || new Date(new Date(invoice.issued_at || invoice.created_at).getTime() + (branding.payment_terms_days || 14) * 24 * 60 * 60 * 1000).toISOString())}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Line Items */}
                <div className="px-4 sm:px-8 py-5">
                    <table className="hidden sm:table w-full">
                        <thead>
                            <tr className="border-b-2 border-line text-[9px] uppercase tracking-[0.1em] text-fg-3">
                                <th className="text-left py-2.5 font-bold">Description</th>
                                <th className="text-right py-2.5 font-bold w-20">Qty</th>
                                <th className="text-right py-2.5 font-bold w-28">Unit Price</th>
                                <th className="text-right py-2.5 font-bold w-32">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                            {invoice.invoice_line_items.map((item) => (
                                <tr key={item.id}>
                                    <td className="py-3">
                                        <span className="font-medium text-fg">{item.description}</span>
                                        {item.item_type && (
                                            <span
                                                className="ml-2 text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide"
                                                style={{ backgroundColor: `${accentColor}12`, color: accentColor }}
                                            >
                                                {item.item_type}
                                            </span>
                                        )}
                                        {extraWorkInvoiceNote(item.approval) && (
                                            <p className="mt-1 text-xs text-fg-3">{extraWorkInvoiceNote(item.approval)}</p>
                                        )}
                                    </td>
                                    <td className="py-3 text-right font-mono tabular-nums text-sm text-fg-3">{item.quantity.toFixed(2)}</td>
                                    <td className="py-3 text-right font-mono tabular-nums text-sm text-fg-3">{formatCurrency(item.unit_price)}</td>
                                    <td className="py-3 text-right font-mono tabular-nums text-sm font-bold text-fg">{formatCurrency(item.line_total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="sm:hidden divide-y divide-line" aria-label="Invoice line items">
                        {invoice.invoice_line_items.map((item) => (
                            <div key={item.id} className="py-4 first:pt-0 last:pb-0">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-fg break-words">{item.description}</p>
                                        {extraWorkInvoiceNote(item.approval) && (
                                            <p className="mt-1 text-xs text-fg-3 break-words">{extraWorkInvoiceNote(item.approval)}</p>
                                        )}
                                        {item.item_type && (
                                            <span
                                                className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide"
                                                style={{ backgroundColor: `${accentColor}12`, color: accentColor }}
                                            >
                                                {item.item_type}
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-mono tabular-nums font-bold text-fg shrink-0">{formatCurrency(item.line_total)}</p>
                                </div>
                                <p className="mt-2 text-xs text-fg-3">
                                    {item.quantity.toFixed(2)} × {formatCurrency(item.unit_price)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Totals */}
                <div className="px-4 sm:px-8 py-5 sm:py-6 bg-raised border-t border-line">
                    <div className="flex justify-end">
                        <div className="w-full sm:w-72 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-fg-3">Subtotal</span>
                                <span className="font-mono tabular-nums text-right text-fg">{formatCurrency(invoice.subtotal)}</span>
                            </div>
                            {branding.is_vat_registered && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-fg-3">VAT (20%)</span>
                                    <span className="font-mono tabular-nums text-right text-fg">{formatCurrency(invoice.vat_amount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-lg font-bold pt-3 pb-1 border-t border-line-strong text-fg">
                                <span>Total</span>
                                <span className="font-mono tabular-nums text-right">{formatCurrency(invoice.total_gross)}</span>
                            </div>
                            {invoice.amount_paid > 0 && (
                                <div className="flex justify-between text-sm text-success">
                                    <span>Amount Paid</span>
                                    <span className="font-mono tabular-nums text-right">-{formatCurrency(invoice.amount_paid)}</span>
                                </div>
                            )}
                            {invoice.balance_due > 0 && (
                                <div data-theme="dark" className="flex justify-between items-center mt-4 p-4 rounded-lg bg-canvas text-fg">
                                    <span className="text-xl font-bold">Balance Due</span>
                                    <span className="text-2xl font-mono tabular-nums font-black text-right text-fg">
                                        {formatCurrency(invoice.balance_due)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Payment Buttons */}
                {showPayButton && onPaymentClick && (
                    <div className="px-4 sm:px-8 py-5 sm:py-6 bg-raised border-t border-line">
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 justify-center">
                            <button
                                onClick={() => onPaymentClick(invoice, 'card')}
                                className="w-full sm:flex-1 sm:max-w-[200px] min-h-[44px] py-3 px-6 rounded-lg font-bold text-on-fill transition-all hover:brightness-110"
                                style={{ backgroundColor: accentColor }}
                            >
                                💳 Pay by Card
                            </button>
                            <button
                                onClick={() => onPaymentClick(invoice, 'cash')}
                                className="w-full sm:flex-1 sm:max-w-[200px] min-h-[44px] py-3 px-6 bg-success-fill rounded-lg font-bold text-on-fill transition-all hover:brightness-110"
                            >
                                💵 Pay Cash
                            </button>
                            <button
                                onClick={() => onPaymentClick(invoice, 'part')}
                                className="w-full sm:flex-1 sm:max-w-[200px] min-h-[44px] py-3 px-6 bg-gold-fill rounded-lg font-bold text-fg transition-all hover:brightness-110"
                            >
                                ➗ Part Payment
                            </button>
                        </div>
                    </div>
                )}

                {/* Bank Details & Footer */}
                {showBankDetails && (
                    <div className="px-4 sm:px-8 py-5 border-t border-line bg-card">
                        <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-fg-3 mb-3">Payment Details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-lg text-sm border border-line bg-raised/80 p-4 rounded-lg">
                            <div>
                                <p className="text-[9px] text-fg-3 uppercase tracking-[0.1em] mb-0.5">Bank Name</p>
                                <p className="font-semibold text-[13px] text-fg">{branding.bank_details?.bank_name}</p>
                            </div>
                            <div>
                                <p className="text-[9px] text-fg-3 uppercase tracking-[0.1em] mb-0.5">Account Name</p>
                                <p className="font-semibold text-[13px] text-fg">{branding.bank_details?.account_name}</p>
                            </div>
                            <div>
                                <p className="text-[9px] text-fg-3 uppercase tracking-[0.1em] mb-0.5">Sort Code</p>
                                <p className="font-mono text-fg font-semibold text-[15px] tracking-[0.15em]">{branding.bank_details?.sort_code}</p>
                            </div>
                            <div>
                                <p className="text-[9px] text-fg-3 uppercase tracking-[0.1em] mb-0.5">Account Number</p>
                                <p className="font-mono text-fg font-semibold text-[15px] tracking-[0.15em]">{branding.bank_details?.account_number}</p>
                            </div>
                        </div>
                    </div>
                )}

                {invoice.notes && (
                    <div className="px-4 sm:px-8 py-4 text-sm text-fg-3 border-t border-line">
                        <p className="font-medium mb-1">Notes:</p>
                        <p className="whitespace-pre-line">{invoice.notes}</p>
                    </div>
                )}

                {!isWhiteLabel && (
                    <div data-theme="dark" className="px-4 sm:px-8 py-4 bg-canvas text-fg-3 text-center text-xs flex items-center justify-center gap-2">
                        <img
                            src="/forgedash-logo.svg"
                            alt="ForgeDash"
                            className="h-4 opacity-50"
                            onError={(e) => e.currentTarget.style.display = 'none'}
                        />
                        <span>Powered by ForgeDash</span>
                    </div>
                )}
            </div>

            <EmailInvoiceModal
                isOpen={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                customerEmail={invoice.customers?.email || ''}
                customerName={invoice.customers?.full_name || ''}
                invoiceNumber={invoice.invoice_number}
                invoiceId={invoice.id}
                orgId={invoice.org_id}
                amountDue={invoice.balance_due}
                businessName={branding.business_name || 'ForgeDash'}
                businessEmail={branding.email}
                generatePdfBase64={generatePdfBase64}
                onSuccess={handleEmailSuccess}
                vrm={invoice.vehicles?.vrm}
                vehicleName={invoice.vehicles ? `${invoice.vehicles.make} ${invoice.vehicles.model}` : undefined}
            />
        </div>
    );
}
